import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { 
  Sparkles, 
  Wand2,
  BookOpen,
  Mic,
  Share2,
  MessageCircle,
  ChevronRight,
  Brain,
  Palette,
  Music,
  Clock,
  CheckCircle2,
  Book
} from 'lucide-react'

const Home = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: Brain,
      title: "智能创作",
      description: "输入简单提示，AI 自动生成完整的绘本故事内容",
      baseColor: "primary",
      gradientFrom: "from-primary/10",
      gradientTo: "to-primary/5",
      hoverFrom: "from-primary/20",
      hoverTo: "to-primary/10"
    },
    {
      icon: Palette,
      title: "精美插画",
      description: "自动生成精美的故事场景插画，多种艺术风格可选",
      baseColor: "rose-500",
      gradientFrom: "from-rose-500/10",
      gradientTo: "to-rose-500/5",
      hoverFrom: "from-rose-500/20",
      hoverTo: "to-rose-500/10"
    },
    {
      icon: Music,
      title: "高级配音",
      description: "专业级语音配音，让故事更加生动有趣",
      baseColor: "violet-500",
      gradientFrom: "from-violet-500/10",
      gradientTo: "to-violet-500/5",
      hoverFrom: "from-violet-500/20",
      hoverTo: "to-violet-500/10"
    },
    {
      icon: BookOpen,
      title: "海量模板",
      description: "精选优质模板，一键套用快速创作绘本",
      baseColor: "amber-500",
      gradientFrom: "from-amber-500/10",
      gradientTo: "to-amber-500/5",
      hoverFrom: "from-amber-500/20",
      hoverTo: "to-amber-500/10"
    },
    {
      icon: Book,
      title: "作品管理",
      description: "便捷管理你的所有绘本作品，支持草稿保存",
      baseColor: "emerald-500",
      gradientFrom: "from-emerald-500/10",
      gradientTo: "to-emerald-500/5",
      hoverFrom: "from-emerald-500/20",
      hoverTo: "to-emerald-500/10"
    },
    {
      icon: Share2,
      title: "分享互动",
      description: "一键分享作品，获得更多互动和反馈",
      baseColor: "blue-500",
      gradientFrom: "from-blue-500/10",
      gradientTo: "to-blue-500/5",
      hoverFrom: "from-blue-500/20",
      hoverTo: "to-blue-500/10"
    }
  ]

  return (
    <div className="w-full space-y-12 pb-16">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="inline-block animate-bounce-slow mb-6">
            <Sparkles className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary animate-rainbow-text">
            让每个孩子都能创作属于自己的绘本
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            使用 AI 智能创作，轻松将你的想象变成精美的有声绘本
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/create')}
              className="bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 text-white px-8"
            >
              开始创作
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/discover')}
              className="px-8 border border-gray-200 hover:border-primary/40 hover:bg-gray-50/80 hover:shadow-sm hover:scale-[1.02] transition-all duration-200"
            >
              浏览作品
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            核心功能
          </h2>
          <p className="text-gray-600">
            让创作绘本变得简单有趣
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className="group relative overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* 默认背景 */}
              <div 
                className={`
                  absolute inset-0 bg-gradient-to-br ${feature.gradientFrom} ${feature.gradientTo}
                  opacity-50 group-hover:opacity-0 transition-opacity duration-300
                `} 
              />
              {/* 悬停背景 */}
              <div 
                className={`
                  absolute inset-0 bg-gradient-to-br ${feature.hoverFrom} ${feature.hoverTo}
                  opacity-0 group-hover:opacity-50 transition-opacity duration-300
                `}
              />
              
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className={`w-6 h-6 text-${feature.baseColor}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                  {feature.description}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="p-12 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            准备好开始创作了吗？
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            加入我们，让创作变得简单而有趣。现在就开始创作属于你的第一本绘本吧！
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/create')}
            className="bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 text-white px-8"
          >
            立即创作
          </Button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-600 mb-1">
              常见问题
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                q: "需要具备绘画能力吗？",
                a: "完全不需要！我们的 AI 会根据你的描述自动生成精美的插画。你只需要描述想要的场景，AI 就能为你创造出精美的画面。"
              },
              {
                q: "创作一本绘本需要多长时间？",
                a: "通常只需要 5-10 分钟就能完成一本简单的绘本创作。你可以随时保存进度，分多次完成创作。"
              },
              {
                q: "可以修改 AI 生成的内容吗？",
                a: "当然可以！你可以随时调整故事内容、修改插画风格或重新生成。我们提供了丰富的编辑功能，让你能够完全掌控创作过程。"
              },
              {
                q: "生成的内容会重复吗？",
                a: "每次生成的内容都是独特的，不会出现完全相同的故事或插画。AI 会根据你的提示生成原创内容，确保每本绘本都是独一无二的。"
              },
              {
                q: "支持哪些配音选项？",
                a: "我们提供多种专业配音选项，包括不同性别、年龄和风格的声音。你可以试听后选择最适合你故事的配音。"
              },
              {
                q: "创作的绘本可以商用吗？",
                a: "可以！你创作的绘本版权完全属于你。你可以自由地使用、分享或出版你的作品。我们建议在商用前仔细阅读用户协议。"
              },
              {
                q: "能导出成其他格式吗？",
                a: "支持导出为 PDF、图片集、视频等多种格式。你可以方便地在各种设备上阅读，或分享到社交媒体。"
              },
              {
                q: "遇到问题怎么办？",
                a: "我们提供全面的帮助文档和在线客服支持。你可以在创作过程中随时寻求帮助，我们会及时为你解答问题。"
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className="py-2.5 border-b border-gray-100/60 last:border-0 hover:bg-white/40 transition-colors rounded-lg px-3"
              >
                <h3 className="font-medium text-gray-600 mb-1 text-sm">{faq.q}</h3>
                <p className="text-xs leading-relaxed text-gray-500">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home 